import { type HTMLAttributes, type ReactNode } from "react";
import { motion, type HTMLMotionProps } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { cn } from "../lib/cn";
import { fadeUp } from "../lib/motion";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Adds a lift on hover. Use only when the whole card is interactive. */
  interactive?: boolean;
  /** Draws the gold accent rail — reserve it for the highlighted item. */
  accent?: "none" | "gold" | "crimson";
  padded?: boolean;
}

export function Card({
  interactive = false,
  accent = "none",
  padded = true,
  className,
  children,
  ...props
}: CardProps) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-xl border border-line bg-surface shadow-ir-sm",
        padded && "p-5",
        interactive &&
          "transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-gold-300 hover:shadow-ir-md dark:hover:border-gold-700",
        className,
      )}
      {...props}
    >
      {accent !== "none" && (
        <span
          aria-hidden="true"
          className={cn(
            "absolute inset-y-0 left-0 w-1",
            accent === "gold" ? "bg-gold-sheen" : "bg-crimson-sheen",
          )}
        />
      )}
      {children}
    </div>
  );
}

export function MotionCard({
  className,
  children,
  ...props
}: HTMLMotionProps<"div">) {
  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      className={cn(
        "relative overflow-hidden rounded-xl border border-line bg-surface p-5 shadow-ir-sm",
        className,
      )}
      {...props}
    >
      {children}
    </motion.div>
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  icon?: LucideIcon;
  actions?: ReactNode;
  className?: string;
}

export function CardHeader({
  title,
  description,
  icon: Icon,
  actions,
  className,
}: CardHeaderProps) {
  return (
    <div className={cn("mb-4 flex items-start justify-between gap-4", className)}>
      <div className="flex min-w-0 items-start gap-3">
        {Icon && (
          <span className="mt-0.5 grid size-9 shrink-0 place-items-center rounded-lg bg-gold-100 text-gold-700 dark:bg-gold-950/60 dark:text-gold-400">
            <Icon size={17} aria-hidden="true" />
          </span>
        )}
        <div className="min-w-0">
          <h3 className="truncate text-base font-semibold text-content">{title}</h3>
          {description && (
            <p className="mt-0.5 text-sm text-content-muted">{description}</p>
          )}
        </div>
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function CardSection({
  title,
  children,
  className,
}: {
  title?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("border-t border-line pt-4 first:border-0 first:pt-0", className)}>
      {title && (
        <h4 className="mb-3 text-xs font-semibold uppercase tracking-[0.08em] text-content-subtle">
          {title}
        </h4>
      )}
      {children}
    </section>
  );
}
